import { LDProvider } from 'launchdarkly-react-client-sdk'
import { createContext, FC, ReactNode, useEffect, useState } from 'react'

interface FeatureFlagProviderProps {
  children: React.ReactNode
}

// Mock LaunchDarkly context for E2E tests
interface MockLDContextValue {
  flags: Record<string, boolean>
  client: {
    identify: (context: Record<string, unknown>) => void
    variation: (key: string, defaultValue: boolean) => boolean
    on: (event: string, callback: () => void) => void
    off: (event: string, callback: () => void) => void
  }
}

export const MockLDContext = createContext<MockLDContextValue | null>(null)

// Mock LDProvider component for E2E tests
const MockLDProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<Record<string, boolean>>({})

  useEffect(() => {
    // Read flags from window object set by Playwright addInitScript
    const mockFlags = window.__MOCK_LAUNCHDARKLY_FLAGS__
    if (mockFlags) {
      setFlags(mockFlags)
    }
  }, [])

  const mockClient = {
    identify: (_context: Record<string, unknown>) => {
      // No-op for tests
    },
    variation: (key: string, defaultValue: boolean): boolean => {
      // Return flag value from mock flags, or default if not found
      return flags[key] ?? defaultValue
    },
    on: (_event: string, _callback: () => void) => {
      // No-op for tests - flag changes don't happen in E2E tests
    },
    off: (_event: string, _callback: () => void) => {
      // No-op for tests
    }
  }

  const contextValue: MockLDContextValue = {
    flags,
    client: mockClient
  }

  return <MockLDContext.Provider value={contextValue}>{children}</MockLDContext.Provider>
}

const FeatureFlagProvider: FC<FeatureFlagProviderProps> = ({ children }) => {
  const ldClientSideID = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID

  // Check if we're in mock mode (E2E tests)
  const isMockMode =
    typeof window !== 'undefined' && window.__MOCK_LAUNCHDARKLY_FLAGS__ !== undefined

  if (isMockMode) {
    return <MockLDProvider>{children}</MockLDProvider>
  }

  return ldClientSideID ? (
    <LDProvider clientSideID={ldClientSideID} reactOptions={{ useCamelCaseFlagKeys: false }}>
      {children}
    </LDProvider>
  ) : (
    <></>
  )
}

export default FeatureFlagProvider
